import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { ShoppingCart, CheckCircle, Star, Eye, Clock } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl, getImageFallback, PLACEHOLDER_IMAGE } from "../utils/imageUrl";
import { useBrandConfigs } from "../lib/useCache";

const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").trim();
};

const handleImgError = (e) => {
    const fallback = getImageFallback(e.target.dataset.original);
    if (fallback && e.target.src !== fallback) {
        e.target.src = fallback;
    } else {
        // Show placeholder SVG for truly broken/deleted images
        e.target.src = PLACEHOLDER_IMAGE;
        e.target.onerror = null; // Prevent infinite loop
    }
};

const STOCK_MAP = {
    "in_stock": { label: "Skladem", color: "text-green-600 bg-green-50" },
    "out_of_stock": { label: "Vyprodáno", color: "text-red-600 bg-red-50" },
    "backorder": { label: "Na objednávku", color: "text-amber-600 bg-amber-50" },
    "on_order": { label: "Na objednávku", color: "text-blue-600 bg-blue-50" },
};

function isSaleActive(product) {
    if (!product.sale_price || product.sale_price >= product.price) return false;
    const now = new Date();
    if (product.sale_start && new Date(product.sale_start) > now) return false;
    if (product.sale_end && new Date(product.sale_end) < now) return false;
    return true;
}

function useCountdown(endDate) {
    const [timeLeft, setTimeLeft] = useState(null);
    useEffect(() => {
        if (!endDate) return;
        const end = new Date(endDate).getTime();
        const tick = () => {
            const diff = end - Date.now();
            if (diff <= 0) { setTimeLeft(null); return; }
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setTimeLeft({ d, h, m, s, total: diff });
        };
        tick();
        const iv = setInterval(tick, 1000);
        return () => clearInterval(iv);
    }, [endDate]);
    return timeLeft;
}

function CountdownBadge({ endDate, compact = false }) {
    const tl = useCountdown(endDate);
    if (!tl) return null;
    if (compact) {
        return (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full" data-testid="countdown-badge">
                <Clock size={8} />
                {tl.d > 0 ? `${tl.d}d ` : ""}{String(tl.h).padStart(2, "0")}:{String(tl.m).padStart(2, "0")}:{String(tl.s).padStart(2, "0")}
            </span>
        );
    }
    return (
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-orange-700 bg-gradient-to-r from-orange-100 to-amber-100 px-2.5 py-1 rounded-full border border-orange-200" data-testid="countdown-badge">
            <Clock size={10} className="text-orange-500" />
            <span>Akce končí za</span>
            {tl.d > 0 && <span className="bg-orange-600 text-white px-1.5 py-0.5 rounded">{tl.d}d</span>}
            <span className="bg-orange-600 text-white px-1.5 py-0.5 rounded font-mono">{String(tl.h).padStart(2, "0")}:{String(tl.m).padStart(2, "0")}:{String(tl.s).padStart(2, "0")}</span>
        </div>
    );
}

export default function ProductCard({ product, viewMode = "grid" }) {
    const { addItem } = useCart();
    const { data: brandConfigs = [] } = useBrandConfigs();

    const stockInfo = STOCK_MAP[product.stock_status] || STOCK_MAP["in_stock"];
    const saleActive = isSaleActive(product);
    const finalPrice = saleActive ? product.sale_price : product.price;
    const hasDiscount = saleActive;
    const discountPct = hasDiscount ? Math.round((1 - product.sale_price / product.price) * 100) : 0;
    const imgSrc = resolveImageUrl(product.main_image) || resolveImageUrl(product.images && product.images[0]) || PLACEHOLDER_IMAGE;
    const brandCfg = product.brand ? brandConfigs.find(b => b.name === product.brand) : null;
    const brandLogoUrl = brandCfg?.logo_url || null;

    const handleAddToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addItem(product, 1);
        toast.success(`${product.name} přidán do košíku`);
    };

    if (viewMode === "list") return <ListCard product={product} stockInfo={stockInfo} finalPrice={finalPrice} hasDiscount={hasDiscount} discountPct={discountPct} imgSrc={imgSrc} onAdd={handleAddToCart} saleActive={saleActive} />;

    return (
        <Link to={`/produkt/${product.slug || product.id}`} data-testid={`product-card-${product.id}`} className="block h-full">
            <div className={`group bg-white rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:shadow-xl hover:shadow-gray-200/60 hover:-translate-y-1 border ${saleActive ? "border-orange-200 shadow-orange-100/50" : "border-gray-100"} hover:border-gray-200`}>
                {/* Image */}
                <div className="relative overflow-hidden bg-gray-50/30" style={{ paddingBottom: "100%" }}>
                    <img
                        src={imgSrc}
                        alt={product.name}
                        data-original={product.main_image}
                        className="absolute inset-0 w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
                        style={{ minWidth: "60%", minHeight: "60%", margin: "auto" }}
                        onError={handleImgError}
                        loading="lazy"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                        <span className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full shadow-lg border border-gray-100">
                            <Eye size={11} /> Detail
                        </span>
                    </div>
                    {/* Badges */}
                    <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
                        {product.is_new && (
                            <span className="text-[10px] bg-blue-600 text-white px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">Nový</span>
                        )}
                        {hasDiscount && (
                            <span className="text-[10px] bg-red-500 text-white px-2.5 py-1 rounded-full font-bold animate-pulse">-{discountPct}%</span>
                        )}
                        {product.is_featured && (
                            <span className="text-[10px] bg-amber-500 text-white px-2.5 py-1 rounded-full font-bold flex items-center gap-0.5">
                                <Star size={8} /> TOP
                            </span>
                        )}
                        {(product.badges || []).map((b, i) => (
                            <span key={i} className="text-[10px] text-white px-2.5 py-1 rounded-full font-bold" style={{ backgroundColor: b.color || "#6b7280" }}>{b.text}</span>
                        ))}
                    </div>
                    {/* Sale countdown top right */}
                    {saleActive && product.sale_end && (
                        <div className="absolute top-2.5 right-2.5">
                            <CountdownBadge endDate={product.sale_end} compact />
                        </div>
                    )}
                    {/* Brand logo overlay - bottom right */}
                    {brandLogoUrl && (
                        <div className="absolute bottom-2 right-2 z-10 bg-white/90 backdrop-blur-sm rounded-lg p-1.5 shadow-sm border border-gray-100"
                            data-testid={`card-brand-logo-${product.id}`}>
                            <img src={brandLogoUrl} alt={product.brand} className="h-6 w-auto object-contain max-w-[60px]" loading="lazy" />
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                    {product.brand && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-green-600 mb-1">{product.brand}</span>
                    )}
                    <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 mb-1 leading-snug group-hover:text-green-700 transition-colors duration-200">
                        {product.name}
                    </h3>

                    {/* Star rating */}
                    {product.avg_rating > 0 && (
                        <div className="flex items-center gap-1 mb-1.5" data-testid={`card-rating-${product.id}`}>
                            <div className="flex gap-px">
                                {[1,2,3,4,5].map(n => (
                                    <Star key={n} size={11}
                                        className={product.avg_rating >= n ? "text-yellow-400 fill-yellow-400" : product.avg_rating >= n - 0.5 ? "text-yellow-400 fill-yellow-200" : "text-gray-200 fill-gray-200"} />
                                ))}
                            </div>
                            <span className="text-[10px] text-gray-400 font-medium">({product.review_count || 0})</span>
                        </div>
                    )}

                    {/* Key params */}
                    {product.parameters && Object.keys(product.parameters).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                            {Object.entries(product.parameters).slice(0, 2).map(([k, v]) => (
                                <span key={k} className="text-[10px] bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full">
                                    {v}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-auto pt-3">
                        {/* Stock */}
                        <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium mb-2 ${stockInfo.color}`}>
                            {product.stock_status === "in_stock" && <CheckCircle size={9} />}
                            {stockInfo.label}
                        </div>

                        {/* Enhanced price with sale indicator */}
                        <div className="flex items-end justify-between gap-2">
                            <div>
                                <p className={`text-lg font-bold ${hasDiscount ? "text-red-600" : "text-gray-900"}`} data-testid="card-price">
                                    {finalPrice?.toLocaleString("cs-CZ")} Kč
                                </p>
                                {hasDiscount && (
                                    <p className="text-xs text-gray-400 line-through">
                                        {product.price?.toLocaleString("cs-CZ")} Kč
                                    </p>
                                )}
                                <p className="text-[10px] text-gray-400">
                                    bez DPH {(finalPrice / 1.21).toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč
                                </p>
                            </div>
                            <button
                                data-testid={`add-to-cart-${product.id}`}
                                onClick={handleAddToCart}
                                disabled={product.stock_status === "out_of_stock"}
                                className="flex items-center gap-1 text-white text-xs font-medium h-9 px-3.5 rounded-full transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95 disabled:opacity-40 disabled:hover:scale-100 flex-shrink-0 bg-green-600 hover:bg-green-700"
                            >
                                <ShoppingCart size={13} />
                                <span className="hidden sm:inline">Košík</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
}

function ListCard({ product, stockInfo, finalPrice, hasDiscount, discountPct, imgSrc, onAdd, saleActive }) {
    return (
        <Link to={`/produkt/${product.slug || product.id}`} data-testid={`product-list-${product.id}`} className="block">
            <div className={`group bg-white rounded-2xl overflow-hidden flex transition-all duration-200 hover:shadow-lg hover:shadow-gray-200/60 border ${saleActive ? "border-orange-200" : "border-gray-100"} hover:border-gray-200`}>
                {/* Image */}
                <div className="w-44 h-44 flex-shrink-0 relative overflow-hidden bg-white">
                    <img src={imgSrc} alt={product.name} data-original={product.main_image}
                        className="w-full h-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
                        onError={handleImgError} loading="lazy" />
                    {hasDiscount && (
                        <span className="absolute top-2 left-2 text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse">-{discountPct}%</span>
                    )}
                    {saleActive && product.sale_end && (
                        <div className="absolute bottom-2 left-2">
                            <CountdownBadge endDate={product.sale_end} compact />
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 p-4 flex flex-col min-w-0">
                    <div className="flex items-start gap-3 mb-1">
                        <div className="flex-1 min-w-0">
                            {product.brand && (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-green-600">{product.brand}</span>
                            )}
                            <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 group-hover:text-green-700 transition-colors">
                                {product.name}
                            </h3>
                        </div>
                        <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${stockInfo.color}`}>
                            {product.stock_status === "in_stock" && <CheckCircle size={9} />}
                            {stockInfo.label}
                        </div>
                    </div>

                    {product.parameters && Object.keys(product.parameters).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                            {Object.entries(product.parameters).slice(0, 4).map(([k, v]) => (
                                <span key={k} className="text-[10px] bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full">{v}</span>
                            ))}
                        </div>
                    )}

                    {product.short_description && (
                        <p className="text-xs text-gray-500 line-clamp-1 mb-2">{stripHtml(product.short_description)}</p>
                    )}

                    <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-baseline gap-2">
                            <span className={`text-lg font-bold ${hasDiscount ? "text-red-600" : "text-gray-900"}`}>
                                {finalPrice?.toLocaleString("cs-CZ")} Kč
                            </span>
                            {hasDiscount && (
                                <span className="text-xs text-gray-400 line-through">{product.price?.toLocaleString("cs-CZ")} Kč</span>
                            )}
                            <span className="text-[10px] text-gray-400">bez DPH {(finalPrice / 1.21).toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč</span>
                        </div>
                        <button
                            data-testid={`add-to-cart-list-${product.id}`}
                            onClick={onAdd}
                            disabled={product.stock_status === "out_of_stock"}
                            className="flex items-center gap-1.5 text-white text-xs font-medium h-9 px-4 rounded-full transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95 disabled:opacity-40 flex-shrink-0 bg-green-600 hover:bg-green-700"
                        >
                            <ShoppingCart size={13} /> Do košíku
                        </button>
                    </div>
                </div>
            </div>
        </Link>
    );
}
